import { coerceSlashDelimitedDate } from "@helpers/mrtm-date-string-formatter.helper";
import type Campaign from "@services/sdk/models/campaign";
import DeliveryProduct from "@services/sdk/models/delivery-product";
import PlanProduct from "@services/sdk/models/plan-product";
import type Product from "@services/sdk/models/product";

import { pipe } from "ramda";

import type {
	AddonProduct,
	BoosterProduct,
	IOnboardingRequestBody,
} from "@services/sdk";

import Address from "./address";

export interface ICreditCardDetails {
	cardholderName: string;
	bin: string;
	lastFour: string;
	expiry: string;
}

export interface IPaymentDetails {
	token: string;
	nonce: string;
	deviceData: string;
}

export enum PlanTypeEnum {
	PREPAID = 0,
	POSTPAID = 1,
}

export enum SIMTypeEnum {
	ESIM = "ESIM",
	PLASTIC = "PHYSICAL",
}

export interface IOnboardingDetails {
	plan?: PlanProduct;
	keepCurrentNumber?: boolean | null;
	useNewNumber?: boolean | null;
	currentNumber?: string | null;
	firstName?: string | null;
	lastName?: string | null;
	dob?: string | null;
	email?: string | null;
	password?: string | null;
	residentialAddress?: Address;
	deliveryAddress?: Address;
	creditCardDetails?: ICreditCardDetails;
	paymentDetails?: IPaymentDetails;
	deliveryMethod?: DeliveryProduct | null;
	selectedProducts?: Array<DeliveryProduct | AddonProduct | BoosterProduct>;
	promoCode?: Campaign | null;
	simType: SIMTypeEnum;
	channel: string;
	msisdn: string | null;
	iccid: string | null;
	productId: string | null;
	simChannel: string | null;
	externalId?: string | null;
}

class OnboardingDetails implements IOnboardingDetails {
	plan: PlanProduct = PlanProduct.ConstructEmpty();

	keepCurrentNumber: boolean | null = null;

	useNewNumber: boolean | null = null;

	currentNumber: string | null = null;

	firstName: string | null = null;

	lastName: string | null = null;

	dob: string | null = null;

	email: string | null = null;

	password: string | null = null;

	residentialAddress: Address = Address.ConstructEmpty();

	deliveryAddress: Address = Address.ConstructEmpty();

	deliveryMethod: DeliveryProduct | null = DeliveryProduct.ConstructEmpty();

	selectedProducts: Array<AddonProduct> = [];

	promoCode: Campaign | null = null;

	creditCardDetails: ICreditCardDetails = {
		cardholderName: "",
		bin: "",
		lastFour: "",
		expiry: "",
	};

	paymentDetails: IPaymentDetails = {
		token: "",
		nonce: "",
		deviceData: "{}",
	};
	msisdn: string | null = null;

	iccid: string | null = null;

	productId: string | null = null;

	simChannel: string | null = null;

	externalId: string | null = null;

	simType: SIMTypeEnum = SIMTypeEnum.ESIM;

	channel = "WEB";

	public static ConstructEmpty(): OnboardingDetails {
		return new OnboardingDetails();
	}

	public static Construct(params?: IOnboardingDetails): OnboardingDetails {
		return new OnboardingDetails(params);
	}

	/**
	 * maps the given model to a POJO of shape defined by the IOnboardingRequestBody interface
	 * @param model
	 * @constructor
	 */
	public static MapToRequestBody(
		model: OnboardingDetails,
	): IOnboardingRequestBody {
		const convertToMRTMDateStr = coerceSlashDelimitedDate;
		let sim: any;
		const validate = (): Array<string> => {
			const nullCheck =
				(
					key: string,
					value: any | null,
				): ((input: Array<string>) => Array<string>) =>
				(errors: Array<string>): Array<string> =>
					value === null ? [...errors, `${key} cannot be null`] : [...errors];

			return pipe(
				nullCheck("firstName", model.firstName),
				nullCheck("lastName", model.lastName),
				nullCheck("email", model.email),
				nullCheck("password", model.password),
				nullCheck("dob", model.dob),
				// nullCheck("deliveryMethod", model.deliveryMethod)
			)([]);
		};

		const validationErrors = validate();
		if (validationErrors.length > 0) {
			throw new Error(JSON.stringify(validationErrors));
		}

		const productOrderItems = [];
		if (model.iccid && model.productId && model.simChannel === "RETAIL") {
			sim = {
				msisdn: model.msisdn,
				iccid: model.iccid,
				productId: model.productId,
				simChannel: model.simChannel,
			};
		}

		// @ts-ignore
		productOrderItems.push({
			productId: model.plan.id,
			type: model.plan.rawType,
		});

		if (model.deliveryMethod && model.deliveryMethod.id) {
			// @ts-ignore
			productOrderItems.push({
				productId: model.deliveryMethod.id,
				type: model.deliveryMethod.rawType,
			});
		}

		productOrderItems.push(
			// @ts-ignore
			...model.selectedProducts.map((product) => ({
				productId: product.id,
				type: product.rawType,
			})),
		);

		return <IOnboardingRequestBody>(<unknown>{
			tnc: {
				revision: "string",
			},
			relatedParty: {
				firstName: model.firstName,
				lastName: model.lastName,
				email: model.email,
				password: model.password,
				dob: convertToMRTMDateStr(<string>model.dob),
			},
			simType: model.simType,
			postalAddress: Address.MapToMRTMAddress(model.deliveryAddress),
			billingAddress: Address.MapToMRTMAddress(model.residentialAddress),
			productOrderItems,
			promoCode: model.promoCode ? model.promoCode.code : null,
			sim,
			payment: {
				nonce: model.paymentDetails.nonce,
				deviceData: model.paymentDetails.deviceData || "{}",
			},
			...(model.keepCurrentNumber && {
				portin: {
					birthdate: convertToMRTMDateStr(<string>model.dob),
					msisdn: model.currentNumber,
				},
			}),
			channel: model.channel,
			...(model.externalId && {
				externalId: model.externalId,
			}),
		});
	}

	public update(params: IOnboardingDetails): OnboardingDetails {
		const updatedOnboardingDetails = OnboardingDetails.Construct(this);
		Object.assign(updatedOnboardingDetails, params);
		return updatedOnboardingDetails;
	}

	constructor(params?: IOnboardingDetails) {
		if (!params) {
			return;
		}

		Object.assign(this, params);
	}
}

export default OnboardingDetails;
